import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Center,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import type { WebhookEndpointDto } from '../api/webhook-endpoints';
import {
  useCreateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useWebhookEndpoints,
} from '../api/webhook-endpoints';

interface WebhookEndpointsCardProps {
  orgId: string;
}

export function WebhookEndpointsCard({ orgId }: WebhookEndpointsCardProps) {
  const endpoints = useWebhookEndpoints(orgId);
  const create = useCreateWebhookEndpoint();
  const del = useDeleteWebhookEndpoint(orgId);

  const [newUrl, setNewUrl] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpointDto | null>(null);
  const [error, setError] = useState('');

  function handleCreate() {
    create.mutate(
      { orgId, url: newUrl },
      {
        onSuccess: (result) => {
          setNewSecret(result.signingSecret);
          setNewUrl('');
          setShowForm(false);
        },
        onError: (err: unknown) => {
          const apiErr = err as { status?: number; body?: unknown };
          setError(
            `Error ${apiErr.status}: ${typeof apiErr.body === 'string' ? apiErr.body : JSON.stringify(apiErr.body)}`,
          );
        },
      },
    );
  }

  function handleDelete() {
    if (!deleting) return;
    del.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
  }

  return (
    <Card withBorder>
      <Group justify="space-between" mb="md">
        <Title order={4}>Webhook endpoints</Title>
        <Button size="xs" variant="light" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : '+ Add endpoint'}
        </Button>
      </Group>

      {error && (
        <Alert color="red" mb="md" role="alert">
          {error}
        </Alert>
      )}

      {newSecret && (
        <Alert color="yellow" mb="md" role="alert">
          <Text fw={500}>Copy this signing secret now — it won't be shown again.</Text>
          <Group mt="xs">
            <Code>{newSecret}</Code>
            <CopyButton value={newSecret}>
              {({ copied, copy }) => (
                <Button size="xs" variant="light" color={copied ? 'teal' : 'blue'} onClick={copy}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Button size="xs" variant="subtle" mt="xs" onClick={() => setNewSecret(null)}>
            Dismiss
          </Button>
        </Alert>
      )}

      {showForm && (
        <Group mb="md" align="flex-end">
          <TextInput
            label="Webhook URL"
            placeholder="https://example.com/webhook"
            value={newUrl}
            onChange={(e) => setNewUrl(e.currentTarget.value)}
            style={{ flex: 1 }}
            required
          />
          <Button loading={create.isPending} onClick={handleCreate} disabled={!newUrl}>
            Create
          </Button>
        </Group>
      )}

      {endpoints.isLoading ? (
        <Center>
          <Loader size="sm" />
        </Center>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>URL</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(endpoints.data ?? []).map((ep) => (
              <Table.Tr key={ep.id}>
                <Table.Td>{ep.url}</Table.Td>
                <Table.Td>{new Date(ep.createdAt).toLocaleDateString()}</Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" color="red" onClick={() => setDeleting(ep)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Delete confirmation */}
      <Modal opened={!!deleting} onClose={() => setDeleting(null)} title="Delete webhook endpoint">
        <Text>
          Delete endpoint <strong>{deleting?.url}</strong>?
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button color="red" loading={del.isPending} onClick={handleDelete}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Card>
  );
}
